<template>
  <StepComponent>
    <v-row align-content="center" justify="center" v-if="!user.is_copy_to">
      <v-col>
        <div>Receive Reports Via</div>
        <div v-if="communicationTypeError" class="error-text">
          {{ communicationTypeError }}
        </div>
        <template v-for="communication_type in filteredCommunicationTypes">
          <v-list-item
            v-if="communication_type.name === 'Fax'"
            :key="communication_type.id"
          >
            <v-tooltip right :disabled="!!client.fax">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-inline-block">
                  <v-checkbox
                    :label="communication_type.name"
                    :value="communication_type.id"
                    v-model="report_communication_types_selected"
                    :id="communication_type.name"
                  />
                </div>
              </template>
              <span>
                A fax number was not entered during the contact information
                step.
              </span>
            </v-tooltip>
          </v-list-item>
          <v-list-item v-else :key="communication_type?.id">
            <v-checkbox
              :label="communication_type.name"
              :value="communication_type.id"
              v-model="report_communication_types_selected"
              :id="communication_type.name"
            />
          </v-list-item>
        </template>
      </v-col>
      <v-col>
        <div>Report Layouts</div>
        <div v-if="reportLayoutError" class="error-text">
          {{ reportLayoutError }}
        </div>
        <template v-for="report_layout in report_layouts">
          <v-list-item :key="report_layout.id">
            <v-checkbox
              :label="report_layout.title"
              :value="report_layout.id"
              v-model="report_layouts_selected"
              :id="report_layout.title.replaceAll(' ', '')"
              item-value="id"
            />
          </v-list-item>
        </template>
      </v-col>
      <v-col>
        <v-select
          :items="unit_types"
          label="Units"
          item-text="name"
          item-value="id"
          no-data-text="No matches were found"
          v-model="client.unit_type_id"
          id="client.unit_type_id"
        >
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item
              v-bind="attrs"
              v-on="on"
              :id="`unit-option-${item.id}`"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>

        <v-checkbox
          label="Hold Report for Entire Package"
          v-model="client.release_full_package"
          disabled
          id="client.release_full_package"
        />
        <v-checkbox
          label="Merge Reports by Farm Name"
          v-model="client.merge_reports_by_farm"
          id="client.merge_reports_by_farm"
        />
      </v-col>
    </v-row>
    <v-row v-else>
      <CopyToReportingPreferences
        @validity-changed="copyToValid = $event"
      ></CopyToReportingPreferences>
    </v-row>
  </StepComponent>
</template>

<script>
import StepComponent from "@/components/registration/AccountSetupSteps/StepComponent";
import { mapActions, mapGetters, mapState } from "vuex";
import { mapFields } from "vuex-map-fields";
import rules from "@/utils/rules";
import CopyToReportingPreferences from "../../copy_to/CopyToReportingPreferences.vue";

export default {
  name: "ReportingPreferences",
  components: { StepComponent, CopyToReportingPreferences },
  data() {
    return {
      copyToValid: false,
      rules: {
        required: [rules.required],
        atleastOne: [rules.atLeastOne]
      },
      valid: true,
      communicationTypeError: "",
      reportLayoutError: ""
    };
  },
  created() {
    this.report_communication_types_selected = this.client.report_communication_types.map(
      type => type.id
    );
  },
  computed: {
    ...mapFields("clients", [
      "items[0].fax",
      "items[0].report_communication_types",
      "items[0].report_layouts",
      "report_communication_types_selected",
      "report_layouts_selected",
      "report_templates_selected",
      "report_template_types_selected",
      "items[0].unit_type_id",
      "items[0].merge_reports_by_farm",
      "items[0].release_full_package",
      "items[0].locale_code",
      "client"
    ]),
    ...mapGetters("baseData", [
      "communication_types",
      "locales",
      "unit_types",
      "report_layouts",
      "report_templates",
      "report_template_types"
    ]),
    ...mapGetters("clients", ["selectedClient"]),
    ...mapState("auth", ["user"]),
    filteredCommunicationTypes() {
      return this.communication_types.filter(
        item => !!item.is_report && item.name !== "Online Only"
      );
    },
    ...mapFields("copyTo", [
      "copy_to_communication_types_selected",
      "copy_to_report_layouts_selected"
    ])
  },
  methods: {
    ...mapActions("clients", ["updateReportingPreferences"]),
    ...mapActions("copyTo", ["updateCopyToReportingPreferences"]),
    ...mapActions("auth", ["getUser"]),
    languageFilter(item, queryText) {
      const name = item.name.toLowerCase();
      const searchText = queryText.toLowerCase();

      return name.indexOf(searchText) > -1;
    },
    beforeContinue() {
      if (!this.report_communication_types_selected?.length) {
        this.communicationTypeError = "You must select at least one option.";
        this.valid = false;
      } else {
        this.communicationTypeError = "";
      }

      if (!this.report_layouts_selected?.length) {
        this.reportLayoutError = "You must select at least one layout.";
        this.valid = false;
      } else {
        this.reportLayoutError = "";
      }

      if (!this.valid) return false;

      if (!this.user.is_copy_to) {
        return this.updateReportingPreferences().catch(e => {
          console.error(e);
          return false;
        });
      } else {
        if (!this.copyToValid) {
          return false;
        }

        return this.updateCopyToReportingPreferences({
          copy_to_id: this.user?.copy_to?.id,
          communication_types: this.copy_to_communication_types_selected,
          report_layouts: this.copy_to_report_layouts_selected
        })
          .then(success => {
            if (success) {
              this.getUser();
            }
            return success;
          })
          .catch(e => {
            console.error(e);
            return false;
          });
      }
    },
    emitValidity() {
      const isValid = !this.user.is_copy_to
        ? this.report_communication_types_selected?.length > 0 &&
          this.report_layouts_selected?.length > 0
        : this.copyToValid;

      this.valid = isValid;
      this.$emit("validity-changed", isValid);
    }
  },
  mounted() {
    this.client = this.selectedClient;
    if (this.locale_code === null) {
      this.locale_code = "en_US";
    }
    this.client.locale_code = this.client.locale_code
      ? this.client.locale_code
      : this.locales.find(item => item.name == "English (Default)").code;

    this.emitValidity();
  },
  watch: {
    selectedClient(newVal) {
      this.client = newVal;
      this.report_communication_types_selected = this.selectedClient.report_communication_types.map(
        item => item.id
      );
    },
    "client.report_communication_types"(newVal) {
      this.report_communication_types_selected = newVal.map(type => type.id);
    },
    "client.id": {
      handler() {
        this.report_communication_types_selected = this.client.report_communication_types.map(
          type => type.id
        );
        this.client.locale_code = this.client.locale_code
          ? this.client.locale_code
          : this.locales.find(item => item.name == "English (Default)").code;
      },
      immediate: true,
      deep: true
    },
    report_communication_types_selected() {
      this.communicationTypeError = this.report_communication_types_selected
        ?.length
        ? ""
        : "You must select at least one option.";

      this.emitValidity();
    },
    report_layouts_selected() {
      this.reportLayoutError = this.report_layouts_selected?.length
        ? ""
        : "You must select at least one layout.";

      this.emitValidity();
    },
    copyToValid() {
      this.emitValidity();
    }
  }
};
</script>

<style scoped>
.error-text {
  color: #ff5252;
  font-size: 12px;
  margin-top: 4px;
}
</style>
